import { Observable, of, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AfterViewInit, ChangeDetectorRef, Component, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnCamera, SnCameraGroup } from '@lib/api/entities';
import { SnCameraGroupsAPI, SnCamerasAPI } from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation';
import { SnDataListComponent } from '@lib/ui/data-list';

@Component({
  selector: 'sn-add-camera-to-group-dialog',
  templateUrl: './add-camera-to-group-dialog.component.html',
  styleUrls: ['./add-camera-to-group-dialog.component.scss'],
})
export class AddCameraToGroupDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('list', { static: false })
  public readonly list: SnDataListComponent<SnCamera>;
  public saving: boolean;
  public tableData: SnCamera[] = [];

  public readonly loadData = this._loadData.bind(this);
  public readonly selectedCameras = this._selectedCameras.bind(this);

  public formFields: SnFormFieldConfig[] = [
    {
      key: 'cameras',
      type: 'chips-autocomplete',
      templateOptions: {
        labelProp: 'name',
        label: t('common.cameras'),
        fetchDataFn: this._fetchData.bind(this),
        separatorKeysCodes: [ENTER, COMMA],
      },
    },
  ];

  public formModel: any = { cameras: [] };
  public _querySearch: string;
  public readonly searchControl: UntypedFormControl = new UntypedFormControl();
  public readonly columns: string[] = ['name', 'location', 'floor', 'owner'];
  private readonly _destroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SnCameraGroup,
    private readonly _dialogRef: MatDialogRef<AddCameraToGroupDialogComponent>,
    private readonly _cameraGroupsAPI: SnCameraGroupsAPI,
    private readonly _camerasAPI: SnCamerasAPI,
    private readonly _cd: ChangeDetectorRef,
    private readonly _zone: NgZone,
  ) {}

  public ngOnInit(): void {
    this.formModel = this.data || {};
    if (!this.formModel.cameras) {
      this.formModel.cameras = [];
    }
    
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe((v) => {
        this._querySearch = v;
        this._reloadData();
      });
  }

  public ngAfterViewInit(): void {
    if (this.list && this.list.dataSource) {
      this.list.dataSource.updated$
        .pipe(takeUntil(this._destroy$))
        .subscribe((data) => {
          // Create a new array reference to force Angular to detect the change
          this.tableData = [...data];
          this._cd.detectChanges();
        });
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onSelected(camera: SnCamera): void {
    if (!this.formModel.cameras) {
      this.formModel.cameras = [];
    }
    const index = this.formModel.cameras.findIndex((cam) => camera.id === cam.id);
    if (index > -1) {
      this.formModel.cameras.splice(index, 1);
    } else {
      this.formModel.cameras.push(camera);
    }
  }

  public submit(): void {
    this.saving = true;
    const data = {
      ...this.formModel,
      camera: this.formModel.cameras.map((cam) => cam.id),
    };
    this._cameraGroupsAPI
      .update(this.formModel.id, data)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.saving = false;
        this._dialogRef.close();
      });
  }

  public clear(): void {
    this.searchControl.reset(null);
  }

  public isSelected(camera: SnCamera): boolean {
    if (!this.formModel?.cameras) {
      return false;
    }
    return this.formModel.cameras.findIndex((cam) => cam.id === camera.id) > -1;
  }

  private _selectedCameras(): Observable<ISnListResponse<SnCamera>> {
    return of({
      count: this.formModel.cameras.length,
      totalPages: 1,
      results: this.formModel.cameras,
    }).pipe(takeUntil(this._destroy$));
  }

  private _reloadData(): void {
    this.list.reference.reload();
  }

  private _fetchData(params: ISnListQueryParameters): Observable<ISnListResponse<SnCamera>> {
    return this._camerasAPI.getCameraList(params);
  }

  private _loadData(params?: ISnListQueryParameters): Observable<ISnListResponse<SnCamera>> {
    params.search = this._querySearch;
    return this._camerasAPI
      .getCameraList(this._prepareQueryParams(params))
      .pipe(takeUntil(this._destroy$));
  }

  private _prepareQueryParams<T extends ISnListQueryParameters>(params: T): T {
    return {
      ordering: {
        active: 'name',
        direction: 'asc',
      },
      ...params,
    };
  }
}
